/**
 * Shared StreamHandler lifecycle factory.
 *
 * Builds a StreamHandler<T> (a Promise augmented with .onMessage/.onConnected/.abort)
 * and owns the entire state-machine: lazy start on first onMessage, single-stream
 * guard, abort/resolve/reject semantics. Transport-specific behaviour (how to get
 * a Response, how to drive the stream, whether to intercept chunks) is supplied via config.
 */
export function createStreamHandlerBase(config) {
  let entryCallback = null;
  let connectedCallback = null;
  let promiseResolve = null;
  let promiseReject = null;
  let abortController = null;
  let streamActive = false;
  const promise = new Promise((resolve, reject) => {
    promiseResolve = resolve;
    promiseReject = reject;
  });
  const handler = Object.assign(promise, {
    onMessage(callback) {
      entryCallback = callback;
      void startStreamIfReady();
      return handler;
    },
    onConnected(callback) {
      connectedCallback = callback;
      return handler;
    },
    abort() {
      if (abortController && streamActive) {
        abortController.abort();
        streamActive = false;
        if (promiseResolve) {
          promiseResolve();
        }
      }
    }
  });
  async function startStreamIfReady() {
    var _config$validate;
    if (!entryCallback || streamActive) {
      return;
    }

    // Synchronous preflight — runs before AbortController creation to match
    // the rejection timing callers expect (e.g. BFF missing-fetcher guard).
    const validationError = (_config$validate = config.validate) === null || _config$validate === void 0 ? void 0 : _config$validate.call(config);
    if (validationError) {
      streamActive = true; // prevent re-entry on a second onMessage call
      config.syncStackError.cause = validationError;
      if (promiseReject) promiseReject(config.syncStackError);
      return;
    }

    // eslint-disable-next-line compat/compat
    abortController = new AbortController();
    streamActive = true;
    const onProgress = data => {
      var _config$interceptData;
      const terminalError = (_config$interceptData = config.interceptData) === null || _config$interceptData === void 0 ? void 0 : _config$interceptData.call(config, data);
      if (terminalError !== undefined) {
        config.syncStackError.cause = terminalError;
        streamActive = false;
        if (promiseReject) promiseReject(config.syncStackError);
        return;
      }
      if (entryCallback && streamActive) {
        entryCallback(data);
      }
    };
    const onError = error => {
      if (!streamActive) return;
      streamActive = false;
      config.syncStackError.cause = config.mapError ? config.mapError(error) : error;
      if (promiseReject) promiseReject(config.syncStackError);
    };
    const onClose = () => {
      if (!streamActive) return;
      streamActive = false;
      if (promiseResolve) promiseResolve();
    };
    try {
      await config.run(abortController.signal, () => {
        var _connectedCallback;
        return (_connectedCallback = connectedCallback) === null || _connectedCallback === void 0 ? void 0 : _connectedCallback();
      }, onProgress, onError, onClose);
    } catch (error) {
      if (!streamActive) return;
      streamActive = false;
      const typedError = error instanceof Error ? error : new Error(String(error));
      config.syncStackError.cause = config.mapError ? config.mapError(typedError) : typedError;
      if (promiseReject) promiseReject(config.syncStackError);
    }
  }
  return handler;
}